import type { IOptionsComposableVueQueryOptions } from '@/types/shared/vue-query'
import type { IModel } from '@/types/rc/miners'
import { getHashboardModels } from '@/services/core.service'
import { generateModelDisplayName, generateModelValueKey } from '@/util/rc'
import { useQuery } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref } from 'vue'

/**
 * Composable for getHashboardModels response, mapped to ElSelect options.
 *
 * @param refetchOnWindowFocus default: true; useQuery refetch on window focus.
 * @param refetchOnMount default: true; useQuery refetch on mount.
 * @returns getHashboardModels useQuery state.
 */
export const useHashboardModelOptions = (
  { refetchOnWindowFocus, refetchOnMount, enabled }: IOptionsComposableVueQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: ref(true),
  }
) => {
  const { isFetching, isRefetching, isError, data, error, refetch } = useQuery({
    queryKey: ['hashboard-model-options'],
    queryFn: async () => {
      const hashboardModels = await getHashboardModels()
      return hashboardModels.map((hashboardModel: IModel) => {
        return {
          label: generateModelDisplayName(hashboardModel.name!, hashboardModel.version),
          value: {
            ...hashboardModel,
            valueKey: generateModelValueKey(hashboardModel.name!, hashboardModel.version),
          },
        } as OptionType
      })
    },
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  })

  return {
    isFetching,
    isRefetching,
    isError,
    data,
    error,
    refetch,
  }
}
